<script setup lang="ts">
import { computed, ref } from 'vue'
import { Portal } from 'portal-vue'
import GwFloat, {
  type FloatPlacement,
  type GwFloatIgnoredAttrs,
} from '@/components/lib/GwFloat/GwFloat.vue'
import GwTransition from '@/components/lib/GwTransition/GwTransition.vue'
import GwMenuBox, { type GwMenuBoxProps } from '@/components/lib/GwMenuNew/GwMenuBox.vue'
import { getScreenUtils } from '@/components/lib/GwUtils/utils_screen'

interface GwFloatboxProps {
  trigger?: 'click' | 'hover'
  appear?: boolean
  isMovable?: boolean
  closeOnContentClick?: boolean
  closeOnClick?: boolean
  hasPullDown?: boolean
  isInside?: boolean
  transitionVariant?: GwMenuBoxProps['transitionVariant']
  boxVariant?: GwMenuBoxProps['variant']
  hasClose?: boolean
  placement?: FloatPlacement
  closeOnScroll?: boolean
  disabled?: boolean
  isPortalNested?: boolean
  contentAttrs?: Record<GwFloatIgnoredAttrs, string>
  hasBackOverlayBlur?: boolean
  offset?: {
    top?: number
    left?: number
    right?: number
    bottom?: number
  }
}

const props = withDefaults(defineProps<GwFloatboxProps>(), {
  trigger: 'click',
  transitionVariant: 'fade',
  appear: true,
  boxVariant: 'box_menu',
})

const emit = defineEmits(['open', 'close'])

const floatRef = ref<InstanceType<typeof GwFloat> | null>(null)

const open = (e?: MouseEvent) => floatRef.value?.open(e)
const close = () => floatRef.value?.close()

defineExpose({ open, close })

const { mobile } = getScreenUtils()
const transitionVariant = computed(() =>
  props.hasPullDown || mobile.value ? 'slidebottom_menu' : props.transitionVariant,
)

const customPositionStyle = computed(() => {
  const options = {
    slidetop: {
      top: `calc(var(--safe-area-inset-top) + ${props.offset?.top || 0}px)`,
      left: `calc(var(--safe-area-inset-left) + ${props.offset?.left || 0}px)`,
    },
    slideright: {
      right: `calc(var(--safe-area-inset-right) + ${props.offset?.right || 0}px)`,
      bottom: `calc(var(--safe-area-inset-bottom) + ${props.offset?.bottom || 0}px)`,
    },
    slidebottom_menu: {
      bottom: `calc(var(--safe-area-inset-bottom) + ${props.offset?.bottom || 0}px)`,
      left: `calc(var(--safe-area-inset-left) + ${props.offset?.left || 0}px)`,
    },
    slideleft: {
      left: `calc(var(--safe-area-inset-left) + ${props.offset?.left || 0}px)`,
      bottom: `calc(var(--safe-area-inset-bottom) + ${props.offset?.bottom || 0}px)`,
    },
    dialog: {
      top: `calc(var(--safe-area-inset-top) + ${props.offset?.top || 0}px)`,
      left: `calc(var(--safe-area-inset-left) + ${props.offset?.left || 0}px)`,
    },
    fade: undefined,
  }
  return options[transitionVariant.value]
})
const portalTargetTo = computed(() =>
  ['slideright', 'slideleft'].includes(transitionVariant.value)
    ? `main_inside`
    : `main_before${props.isPortalNested ? 1 : ''}`,
)
</script>
<template>
  <GwFloat
    ref="floatRef"
    :trigger
    :placement
    :customPositionStyle
    :isMovable
    :closeOnContentClick
    :closeOnClick
    :closeOnScroll
    :disabled
    @open="emit('open')"
    @close="emit('close')"
  >
    <template #opener="{ listeners, methods, openId }">
      <slot
        name="opener"
        :listeners
        :methods
        :openId
        :isOpen="!!openId"
      >
        <!-- <div v-on="listeners">opener</div> -->
      </slot>
    </template>
    <template #default="{ attrs, listeners, openId, methods }">
      <!-- recursive portals dont work -->
      <!-- possible solution for this would be that each instance of a menu would create its own portal-target on app level via vuex -->
      <!-- https://github.com/LinusBorg/portal-vue/issues/102 -->
      <GwTransition
        v-if="isInside"
        :appear
        :variant="transitionVariant"
      >
        <slot
          v-if="openId"
          name="default"
          :attrs
          :listeners
        >
          <GwMenuBox
            v-bind="{ ...attrs, ...(contentAttrs || {}) }"
            :variant="boxVariant"
            :transitionVariant
            :hasClose="hasClose || mobile"
            :hasPullDown="hasPullDown || mobile"
            :hasBackOverlay="hasPullDown || transitionVariant === 'dialog' || mobile"
            :tabindex="1"
            v-on="listeners"
            @close="methods.close()"
          >
            <slot
              name="content"
              :methods
            />
          </GwMenuBox>
        </slot>
      </GwTransition>
      <portal
        v-else
        :to="portalTargetTo"
      >
        <GwTransition
          :appear
          :variant="transitionVariant"
        >
          <slot
            v-if="openId"
            name="default"
            :attrs
            :listeners
          >
            <GwMenuBox
              v-bind="{ ...attrs, ...(contentAttrs || {}) }"
              :variant="boxVariant"
              :transitionVariant
              :hasClose="hasClose || mobile"
              :hasPullDown="hasPullDown || mobile"
              :hasBackOverlay="hasPullDown || transitionVariant === 'dialog' || mobile"
              :hasBackOverlayBlur
              :tabindex="1"
              v-on="listeners"
              @close="methods.close()"
            >
              <slot
                name="content"
                :methods
              />
            </GwMenuBox>
          </slot>
        </GwTransition>
      </portal>
    </template>
  </GwFloat>
</template>
